import React, { useRef, useState } from 'react';
import { Loader2, Save, X } from 'lucide-react';
import { pruefeMitarbeiterEingabe } from '../../../services/sifa/kundenportalLogic';
import type { PortalMitarbeiter, PortalMitarbeiterEingabe } from '../../../services/sifa/kundenportalTypes';
import { EINGABE, FEHLERKASTEN, KNOPF_PRIMAER, KNOPF_SEKUNDAER } from '../public/teilnahmeSeiteStil';

/**
 * Anlegen/Bearbeiten einer Person durch den Kunden. Dieselben Felder wie im
 * Innendienst-Dialog (MitarbeiterDialog), plus der Schalter „aktiv" —
 * Deaktivieren ist der einzige „Loeschweg" des Kunden.
 */
export const PortalMitarbeiterDialog: React.FC<{
  /** null = neu anlegen. */
  mitarbeiter: PortalMitarbeiter | null;
  onClose: () => void;
  onSpeichern: (eingabe: PortalMitarbeiterEingabe) => Promise<{ ok: boolean; text: string | null }>;
}> = ({ mitarbeiter, onClose, onSpeichern }) => {
  const [vorname, setVorname] = useState(mitarbeiter?.vorname || '');
  const [nachname, setNachname] = useState(mitarbeiter?.nachname || '');
  const [geburtsdatum, setGeburtsdatum] = useState(mitarbeiter?.geburtsdatum || '');
  const [email, setEmail] = useState(mitarbeiter?.email || '');
  const [abteilung, setAbteilung] = useState(mitarbeiter?.abteilung || '');
  const [personalnummer, setPersonalnummer] = useState(mitarbeiter?.personalnummer || '');
  const [aktiv, setAktiv] = useState(mitarbeiter ? mitarbeiter.aktiv : true);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const laeuft = useRef(false);

  const speichern = async (e: React.FormEvent) => {
    e.preventDefault();
    if (laeuft.current) return;
    const eingabe: PortalMitarbeiterEingabe = {
      id: mitarbeiter?.id ?? null,
      vorname,
      nachname,
      geburtsdatum: geburtsdatum || null,
      email: email || null,
      abteilung: abteilung || null,
      personalnummer: personalnummer || null,
      aktiv,
    };
    const hindernis = pruefeMitarbeiterEingabe(eingabe);
    if (hindernis) {
      setFehler(hindernis);
      return;
    }
    laeuft.current = true;
    setSpeichert(true);
    setFehler(null);
    const ergebnis = await onSpeichern(eingabe);
    laeuft.current = false;
    setSpeichert(false);
    if (ergebnis.ok) onClose();
    else setFehler(ergebnis.text || 'Speichern fehlgeschlagen.');
  };

  const label = 'block text-xs font-bold uppercase text-slate-500 mb-1';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <form
        onSubmit={speichern}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md max-h-[95dvh] overflow-y-auto"
      >
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="text-base font-bold text-slate-800">{mitarbeiter ? 'Mitarbeiter bearbeiten' : 'Mitarbeiter anlegen'}</h3>
          <button type="button" onClick={onClose} aria-label="Schließen" className="p-2 hover:bg-slate-100 rounded-full">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={label} htmlFor="kp-vorname">Vorname *</label>
              <input id="kp-vorname" value={vorname} onChange={(e) => setVorname(e.target.value)} className={EINGABE} autoFocus maxLength={200} autoComplete="off" />
            </div>
            <div>
              <label className={label} htmlFor="kp-nachname">Nachname *</label>
              <input id="kp-nachname" value={nachname} onChange={(e) => setNachname(e.target.value)} className={EINGABE} maxLength={200} autoComplete="off" />
            </div>
            <div>
              <label className={label} htmlFor="kp-geburtsdatum">Geburtsdatum</label>
              <input id="kp-geburtsdatum" type="date" value={geburtsdatum} onChange={(e) => setGeburtsdatum(e.target.value)} className={EINGABE} />
            </div>
            <div>
              <label className={label} htmlFor="kp-personalnummer">Personalnummer</label>
              <input id="kp-personalnummer" value={personalnummer} onChange={(e) => setPersonalnummer(e.target.value)} className={EINGABE} maxLength={100} autoComplete="off" />
            </div>
            <div className="sm:col-span-2">
              <label className={label} htmlFor="kp-email">E-Mail</label>
              <input id="kp-email" type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} className={EINGABE} maxLength={320} autoComplete="off" />
              <p className="text-xs text-slate-500 mt-1">Über die E-Mail werden Unterweisungen der Person zugeordnet und Nachweise zugestellt.</p>
            </div>
            <div className="sm:col-span-2">
              <label className={label} htmlFor="kp-abteilung">Abteilung</label>
              <input id="kp-abteilung" value={abteilung} onChange={(e) => setAbteilung(e.target.value)} className={EINGABE} maxLength={200} autoComplete="off" />
            </div>
          </div>

          <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl min-h-[52px] cursor-pointer">
            <input type="checkbox" checked={aktiv} onChange={(e) => setAktiv(e.target.checked)} className="w-5 h-5 rounded" />
            <span className="text-sm">
              <span className="font-semibold text-slate-800">Aktiv im Betrieb</span>
              <span className="block text-slate-500">Ausgeschiedene Personen werden deaktiviert, nicht gelöscht — ihre Nachweise bleiben erhalten.</span>
            </span>
          </label>

          {fehler && <div className={FEHLERKASTEN}>{fehler}</div>}
        </div>
        <div className="p-4 border-t border-slate-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button type="button" onClick={onClose} className={KNOPF_SEKUNDAER}>Abbrechen</button>
          <button type="submit" disabled={speichert} className={`${KNOPF_PRIMAER} flex items-center justify-center gap-2`}>
            {speichert ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Speichern
          </button>
        </div>
      </form>
    </div>
  );
};
